/**
 * Input manual da REV DECOMP (Task #210) — botão compacto no Controle PLD →
 * Visão Geral que abre um form pequeno pra inserir a REV vigente (a Mesa recebe
 * no WhatsApp). POST /api/v2/pld/decomp (UPSERT source='manual'); o backend
 * deriva `vigente_desde` do calendário operativo quando omitido.
 *
 * Em sucesso, chama `onSaved()` — o pai invalida o cache da projeção e força o
 * refetch (Visão Geral re-renderiza com a nova âncora). Não é role-gated (auth
 * simples atual; revisitar com Cognito v2) — aceitável pro MVP interno.
 */
import { useState } from "react";
import { postPldDecomp } from "../../../api/client";
import type { PldSubmercado } from "../../../api/types";

// Limites CCEE (espelham o backend pld_modelo_v15) — só pra aviso suave.
const PLD_MIN = 57.31;
const PLD_MAX = 785.27;
const REVISOES = ["REV0", "REV1", "REV2", "REV3", "REV4", "REV5"];
const REVISAO_RX = /^REV\d+$/;

/** "YYYY-MM" do mês seguinte. A REV0 do próximo mês é a única cujo `vigente_desde`
 * cai DENTRO do mês corrente (ex. 27/06 p/ jul) — ancora os dias finais deste mês
 * na projeção (#227/#215). */
function proximoMesRef(mesRef: string): string {
  const [y, m] = mesRef.split("-").map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
}

function alvoBtnCls(active: boolean): string {
  return [
    "text-[11px] font-semibold rounded-md px-2 py-1 border transition-colors",
    active
      ? "bg-indra-turquesa/20 text-indra-turquesa border-indra-turquesa/40"
      : "text-white/50 border-white/10 hover:text-white/80",
  ].join(" ");
}

interface Props {
  mesRef: string;
  submercado: PldSubmercado;
  onSaved: () => void;
}

type Feedback = { tone: "ok" | "err"; msg: string } | null;

export function AtualizarDecompForm({ mesRef, submercado, onSaved }: Props) {
  const [open, setOpen] = useState(false);
  // #227: alvo da REV — mês corrente (default, comportamento atual) ou próximo
  // mês (REV0), pra fechar os dias cross-month do mês corrente na projeção.
  const [alvoMes, setAlvoMes] = useState<"corrente" | "proximo">("corrente");
  const [revisao, setRevisao] = useState("REV1");
  const [valor, setValor] = useState("");
  const [vigenteDesde, setVigenteDesde] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const proximo = alvoMes === "proximo";
  const mesRefAlvo = proximo ? proximoMesRef(mesRef) : mesRef;
  // No próximo mês só a REV0 faz sentido (única com vigente_desde no mês corrente).
  const revisaoEnviada = proximo ? "REV0" : revisao;
  const valorNum = Number(valor);
  const valorValido = valor.trim() !== "" && Number.isFinite(valorNum) && valorNum > 0;
  const revisaoValida = REVISAO_RX.test(revisaoEnviada);
  const foraDaFaixa = valorValido && (valorNum < PLD_MIN || valorNum > PLD_MAX);
  const podeSalvar = valorValido && revisaoValida && !submitting;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!podeSalvar) return;
    setSubmitting(true);
    setFeedback(null);
    // .then/.catch (não async/await) pra anexar o handler de rejeição de forma
    // síncrona na promise retornada — evita janela de "unhandled rejection".
    postPldDecomp({
      mes_ref: mesRefAlvo,
      revisao: revisaoEnviada,
      submercado,
      valor: valorNum,
      vigente_desde: vigenteDesde.trim() || undefined,
    })
      .then((res) => {
        setFeedback({
          tone: "ok",
          msg: `${res.revisao} ${res.action === "updated" ? "atualizada" : "salva"} `
            + `(vigente ${res.vigente_desde}). Projeção atualizada.`,
        });
        setValor("");
        onSaved();
      })
      .catch((err: Error) => {
        setFeedback({ tone: "err", msg: `Não foi possível salvar: ${err.message}` });
      })
      .finally(() => setSubmitting(false));
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="pld-decomp-toggle"
        className="text-[11px] font-semibold text-indra-turquesa hover:text-indra-turquesa/80 border border-indra-turquesa/30 rounded-md px-2 py-1 mb-4"
      >
        + Atualizar DECOMP
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="pld-decomp-form"
      className="mb-4 rounded-xl border border-indra-turquesa/30 bg-indra-azul-surface p-4 max-w-xl"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white font-semibold text-sm">Atualizar DECOMP (REV vigente)</h4>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="pld-decomp-close"
          className="text-white/40 hover:text-white text-xs"
        >
          Fechar
        </button>
      </div>

      {/* #227: alvo da REV — corrente (qualquer REV) ou REV0 do próximo mês. */}
      <div className="flex items-center gap-2 mb-2" role="group" aria-label="Mês alvo da REV">
        <button
          type="button"
          onClick={() => setAlvoMes("corrente")}
          data-testid="pld-decomp-alvo-corrente"
          className={alvoBtnCls(!proximo)}
        >
          Mês corrente
        </button>
        <button
          type="button"
          onClick={() => setAlvoMes("proximo")}
          data-testid="pld-decomp-alvo-proximo"
          className={alvoBtnCls(proximo)}
        >
          Próximo mês (REV0)
        </button>
      </div>
      {proximo && (
        <p
          className="text-amber-300/70 text-[11px] mb-3"
          data-testid="pld-decomp-proximo-hint"
        >
          REV0 do próximo mês ({mesRefAlvo}) — cobre os dias finais deste mês na projeção.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] text-white/60 flex flex-col gap-1">
          Mês ref.
          <input
            type="text"
            value={mesRefAlvo}
            readOnly
            data-testid="pld-decomp-mesref"
            className="bg-indra-azul-deep border border-white/10 rounded px-2 py-1 text-white/80 text-sm"
          />
        </label>
        <label className="text-[11px] text-white/60 flex flex-col gap-1">
          Submercado
          <input
            type="text"
            value={submercado}
            readOnly
            data-testid="pld-decomp-submercado"
            className="bg-indra-azul-deep border border-white/10 rounded px-2 py-1 text-white/80 text-sm"
          />
        </label>
        <label className="text-[11px] text-white/60 flex flex-col gap-1">
          Revisão
          <select
            value={revisaoEnviada}
            onChange={(e) => setRevisao(e.target.value)}
            disabled={proximo}
            data-testid="pld-decomp-revisao"
            className="bg-indra-azul-deep border border-white/10 rounded px-2 py-1 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {REVISOES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="text-[11px] text-white/60 flex flex-col gap-1">
          Valor (R$/MWh)
          <input
            type="number"
            step="0.01"
            min="0"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            data-testid="pld-decomp-valor"
            className="bg-indra-azul-deep border border-white/10 rounded px-2 py-1 text-white text-sm"
          />
        </label>
      </div>

      {foraDaFaixa && (
        <p className="text-amber-300/80 text-[11px] mt-2" data-testid="pld-decomp-valor-warning">
          Valor fora da faixa CCEE [R$ {PLD_MIN} – R$ {PLD_MAX}] — confira antes de salvar.
        </p>
      )}

      <button
        type="button"
        onClick={() => setAdvOpen((v) => !v)}
        data-testid="pld-decomp-adv-toggle"
        className="text-[11px] text-white/40 hover:text-white/70 mt-3"
      >
        {advOpen ? "▾" : "▸"} Avançado (vigência)
      </button>
      {advOpen && (
        <label className="text-[11px] text-white/60 flex flex-col gap-1 mt-2 max-w-xs">
          Vigente desde
          <input
            type="date"
            value={vigenteDesde}
            onChange={(e) => setVigenteDesde(e.target.value)}
            data-testid="pld-decomp-vigente"
            className="bg-indra-azul-deep border border-white/10 rounded px-2 py-1 text-white text-sm"
          />
          <span className="text-white/40">
            Deixe vazio pra derivar da semana operativa (calendário ONS).
          </span>
        </label>
      )}

      <div className="flex items-center gap-3 mt-4">
        <button
          type="submit"
          disabled={!podeSalvar}
          data-testid="pld-decomp-submit"
          className="text-xs font-semibold bg-indra-turquesa/20 text-indra-turquesa border border-indra-turquesa/40 rounded-md px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Salvando…" : "Salvar REV"}
        </button>
        {feedback && (
          <span
            data-testid="pld-decomp-feedback"
            className={`text-[11px] ${feedback.tone === "ok" ? "text-indra-positivo" : "text-indra-negativo"}`}
          >
            {feedback.msg}
          </span>
        )}
      </div>
    </form>
  );
}
